import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "../store/auth";
import { api, ApiError } from "../lib/api";
import { acceptSession, type AuthResponse } from "../lib/session";
import { ErrorText, inputCls } from "../components/ui";

const DEMO_ACCOUNTS = [
  { email: "sarah@demo.nihongo-platform.jp", name: "Sarah Williams", role: "先生", note: "Japanese 101 担当" },
];

export function LoginPage() {
  const { t } = useTranslation(["common", "admin"]);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    // 前の人の状態を残さない
    useAuthStore.getState().clear();
    queryClient.clear();
    try {
      const data = await api<AuthResponse>("/api/auth/login", {
        method: "POST",
        body: { identifier: identifier.trim(), password },
      });
      const home = acceptSession(data);
      if (!home) {
        setError(t("admin:login_not_admin"));
        return;
      }
      navigate(home, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) setError(t("admin:login_contract"));
      else setError(err instanceof ApiError ? err.message : t("admin:login_failed"));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8 bg-gradient-to-br from-gray-50 to-sakura/20">
      <div className="w-full max-w-sm">
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-ops/10 text-ops mb-2">
              <span className="text-xl font-bold">N</span>
            </div>
            <h1 className="text-xl font-bold text-primary">{t("common:app_name")}</h1>
          </div>
          <form onSubmit={onSubmit} className="space-y-4">
            <label className="block">
              <span className="text-sm text-muted">{t("admin:login_identifier")}</span>
              <input
                data-testid="login-identifier"
                name="identifier"
                type="text"
                autoComplete="username"
                autoCapitalize="none"
                required
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                className={inputCls}
              />
            </label>
            <label className="block">
              <span className="text-sm text-muted">{t("common:password")}</span>
              <input
                data-testid="login-password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputCls}
              />
            </label>
            {error && <ErrorText testId="login-error">{error}</ErrorText>}
            <button
              data-testid="login-submit"
              type="submit"
              disabled={loading}
              className="w-full bg-ops text-white py-2 rounded-lg hover:bg-ops/90 disabled:opacity-50"
            >
              {loading ? t("common:loading") : t("common:login")}
            </button>
          </form>
        </div>

        <div className="bg-white rounded-2xl shadow mt-4 overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100">
            <span className="text-sm font-bold text-primary">デモアカウントで試す</span>
          </div>
          <p className="px-5 py-2 text-xs text-muted bg-gray-50">
            押すと入力欄に入ります。パスワードは <code className="bg-white px-1 rounded">demo1234</code>
          </p>
          {DEMO_ACCOUNTS.map((d) => (
            <button
              key={d.email}
              type="button"
              onClick={() => { setIdentifier(d.email); setPassword("demo1234"); }}
              className="w-full px-5 py-3 text-left hover:bg-ops/5 flex items-center justify-between border-t border-gray-100"
            >
              <div>
                <div className="text-sm font-bold text-body">{d.name}</div>
                <div className="text-xs text-muted">{d.email}</div>
              </div>
              <div className="text-right">
                <div className="text-xs font-bold text-ops">{d.role}</div>
                <div className="text-xs text-muted">{d.note}</div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
